// Where windows open, how small they may get, and how they behave when the
// screen is too narrow for a floating desktop window.
//
// This lived as hardcoded 800x600 literals in three places in App.jsx and a
// fourth pair of constants in WindowManager. On a phone that meant a window
// wider than the screen, which the drag clamp then pinned at x=0 so it could
// not be moved at all. Keeping the numbers in one place means the spawn size,
// the reset size and the resize floor can never disagree.

// The taskbar is h-14 (56px) and sits 4px off the bottom edge. Reserve a little
// more so a window never opens flush against it.
export const TASKBAR_RESERVED = 64

// Below this width there is no useful room for a floating window: the screen is
// narrower than a comfortable window plus its margins.
export const NARROW_VIEWPORT = 768

// Inset from the screen edge when a window fills a narrow screen. Small enough
// that the window still reads as a window rather than a full-screen takeover.
const NARROW_MARGIN = 8

// Breathing room around a cascaded window on a roomy screen.
const DESKTOP_MARGIN = 40

const DEFAULT_SIZE = { width: 800, height: 600 }

// Sizes each app would like on a screen with room to spare. Treated as an upper
// bound, never as a guarantee.
const PREFERRED_SIZE = {
  crumb: { width: 700, height: 720 },
  terminal: { width: 920, height: 550 },
  arkanoid: { width: 600, height: 860 },
  tetris: { width: 820, height: 750 },
  snake: { width: 880, height: 750 },
  minesweeper: { width: 800, height: 880 },
  tictactoe: { width: 540, height: 680 },
  chess: { width: 840, height: 750 },
  spaceinvaders: { width: 640, height: 820 }
}

const viewport = () => ({
  width: globalThis.innerWidth || 1280,
  height: globalThis.innerHeight || 800
})

export const isNarrowViewport = () => viewport().width < NARROW_VIEWPORT

/** The area a window may occupy, once the taskbar has taken its share. */
export function getAvailableArea() {
  const { width, height } = viewport()
  return { width, height: Math.max(120, height - TASKBAR_RESERVED) }
}

export function getPreferredSize(appName) {
  return PREFERRED_SIZE[appName] || DEFAULT_SIZE
}

/**
 * The smallest a window may be resized to.
 *
 * The old fixed 300x200 floor was larger than some phone screens once the
 * taskbar was accounted for, which made the clamp fight itself.
 */
export function getMinWindowSize() {
  const area = getAvailableArea()
  return {
    minWidth: Math.max(160, Math.min(300, area.width - NARROW_MARGIN * 2)),
    minHeight: Math.max(120, Math.min(200, area.height - NARROW_MARGIN * 2))
  }
}

/** Clamp a size so it can always fit on the current screen. */
export function clampSize(size) {
  const area = getAvailableArea()
  const { minWidth, minHeight } = getMinWindowSize()
  return {
    width: Math.max(minWidth, Math.min(size.width, area.width - NARROW_MARGIN * 2)),
    height: Math.max(minHeight, Math.min(size.height, area.height - NARROW_MARGIN * 2))
  }
}

/** Clamp a position so the window stays reachable on the current screen. */
export function clampPosition(position, size) {
  const area = getAvailableArea()
  return {
    x: Math.max(0, Math.min(position.x, area.width - size.width)),
    y: Math.max(0, Math.min(position.y, area.height - size.height))
  }
}

/**
 * Where a newly opened window should sit.
 *
 * @param {string} appName Which app, so it can honour that app's preferred size.
 * @param {number} index How many windows are already open, for the cascade.
 */
export function getDefaultGeometry(appName, index = 0) {
  const area = getAvailableArea()

  if (isNarrowViewport()) {
    // Fill the screen inside a small margin, and skip the cascade entirely.
    // Offsetting each window by 30px on a 375px screen just walks the second
    // one off the edge.
    return {
      position: { x: NARROW_MARGIN, y: NARROW_MARGIN },
      size: {
        width: area.width - NARROW_MARGIN * 2,
        height: area.height - NARROW_MARGIN * 2
      }
    }
  }

  const preferred = getPreferredSize(appName)
  const size = {
    width: Math.min(preferred.width, area.width - DESKTOP_MARGIN),
    height: Math.min(preferred.height, area.height - DESKTOP_MARGIN)
  }

  const offset = index * 30
  return {
    position: clampPosition({ x: 100 + offset, y: 80 + offset }, size),
    size
  }
}

/**
 * Pull a window back into view. Used after a rotation or window resize, where
 * a window laid out for the old viewport can end up partly or wholly offscreen.
 */
export function fitToViewport(win) {
  if (win.isMaximized) return win

  const size = clampSize(win.size)
  const position = clampPosition(win.position, size)

  if (
    size.width === win.size.width &&
    size.height === win.size.height &&
    position.x === win.position.x &&
    position.y === win.position.y
  ) {
    return win
  }

  return { ...win, size, position }
}
